// Гейты интерфейса поверх прав, ПОСЧИТАННЫХ СЕРВЕРОМ (#712, ТЗ §3.2–§3.3): ролевой
// модели на фронте нет — предикаты читают served `capabilities` сессии (auth-стор),
// включая superadmin-байпас, который инфлейтит бэк. Позиционные хелперы в конце — про
// даты и геометрию позиций, не про права. Реальный барьер — Pundit и policy_scope.
import type { Schemas, User } from '@/api/client'
import type { DepartmentKind } from '@/stores/orgStructure'

export type Position = Schemas['Position']
export type Role = Position['role']

/** Права смотрящего от сервера (`/auth/me`, вход, refresh) — их хранит auth-стор. */
export type SessionCapabilities = Schemas['SessionCapabilities']

/** Права смотрящего либо их отсутствие: `null` — сессии нет, все гейты закрыты. */
export type ViewerCapabilities = SessionCapabilities | null

/** Запись справочника ролей (`GET /meta/roles`) — свойства роли для визарда найма. */
export type RoleCatalogEntry = Schemas['RoleCatalogEntry']

// Тип-уровневый список verb'ов (зеркало Position::CAPABILITY_VERBS): держит опечатку
// в `meta.capability` реестра разделов на компиляции. Рантайм-источник — served verbs.
export type CapabilityVerb =
  | 'view_records'
  | 'manage_records'
  | 'view_audit'
  | 'view_reports'
  | 'manage_users'
  | 'manage_structure'
  | 'assign_positions'
  | 'delete_counterparty'

export type EventKind = Schemas['Event']['kind']
export type DealKind = Schemas['Deal']['kind']

/** Охват позиции (§3.2): отдел ⊂ филиал ⊂ дивизион ⊂ холдинг. Шаг 1 визарда найма. */
export type PositionScope = 'department' | 'branch' | 'division' | 'holding'

// Порядок охватов на шаге 1 визарда — константа интерфейса: бэк хранит зерно роли
// (`grain`), перечня охватов в контракте нет.
export const POSITION_SCOPES: PositionScope[] = ['department', 'branch', 'division', 'holding']

// Возможность verb хоть где (зеркало #can?(verb, :any)): ключ в served `verbs` = право
// есть. Ресурс не спрашиваем осознанно (решение 2026-07-25) — гейтим разделы, не сущности.
export function can(capabilities: ViewerCapabilities, verb: CapabilityVerb): boolean {
  return capabilities !== null && Object.hasOwn(capabilities.verbs, verb)
}

/** Уровень смотрящего (0 ресепшн … 3 холдинг); -1 — прав нет / сессия не поднята. */
export function viewerLevel(capabilities: ViewerCapabilities): number {
  return capabilities?.level ?? -1
}

/** Роль старшей активной позиции — домашний экран и рабочее место; null — нет позиций. */
export function primaryRole(capabilities: ViewerCapabilities): Role | null {
  return capabilities?.primary_role ?? null
}

/** Право на админ-CRUD оргструктуры (§3.2): учредитель / коммдир, не РКСО. */
export function isOrgAdmin(capabilities: ViewerCapabilities): boolean {
  return can(capabilities, 'manage_structure')
}

/** Право найма (§3.2, #638): холдинг, РКСО, дивизион, директор филиала/КЦ. */
export function isUserAdmin(capabilities: ViewerCapabilities): boolean {
  return can(capabilities, 'manage_users')
}

/** Доступ к журналу аудита (§4.9) — руководители всех уровней, включая РКСО. */
export function canAccessAuditLog(capabilities: ViewerCapabilities): boolean {
  return can(capabilities, 'view_audit')
}

/** Право стереть контрагента (§GDPR, зеркало CounterpartyPolicy#destroy?). */
export function canEraseCounterparty(capabilities: ViewerCapabilities): boolean {
  return can(capabilities, 'delete_counterparty')
}

// Админ учётки (§4.11, #638) — гейт сброса пароля / 2FA / деактивации. Конкретную
// учётку не сверяем: карточка достижима лишь для управляемых смотрящим (Scope на бэке).
export function isAccountAdmin(capabilities: ViewerCapabilities): boolean {
  return capabilities?.account_admin === true
}

/** Сотрудник КЦ (зеркало #call_center_staff?) — UI-гейт КЦ-аналитики (#549). */
export function isCallCenter(capabilities: ViewerCapabilities): boolean {
  return capabilities?.call_center === true
}

// Служебный superuser — СТАТУС аккаунта, не право (#639, маркер в справке). Гейты прав
// его не спрашивают: байпас приходит с сервера ([[frontend-superadmin-parity]]).
export function isSuperadmin(user: User | null): boolean {
  return user?.superadmin === true
}

/** Типы событий, доступные смотрящему на создание (§3.2). */
export function creatableEventKinds(capabilities: ViewerCapabilities): EventKind[] {
  return capabilities?.creatable.event_kinds ?? []
}

// Типы сделок (§3.2): без `branchId` — «создаёт ли вообще»; с ним — набор филиала
// (право типа не переносится между филиалами, §3.3.2; холдинг создаёт везде).
export function creatableDealKinds(
  capabilities: ViewerCapabilities,
  branchId?: number,
): DealKind[] {
  if (capabilities === null) return []
  const { creatable } = capabilities
  if (branchId === undefined || creatable.deal_kinds_anywhere) return creatable.deal_kinds
  return creatable.deal_kinds_by_branch[String(branchId)] ?? []
}

/** Филиалы, где смотрящий вправе создавать записи (§3.3); порядок — входного списка. */
export function postableBranches<T extends { id: number }>(
  capabilities: ViewerCapabilities,
  branches: T[],
): T[] {
  return branchesIn(branches, capabilities?.branches.postable)
}

/** Филиалы, доступные на чтение записей — в т.ч. подписка на real-time ленту. */
export function accessibleBranchIds(capabilities: ViewerCapabilities): number[] {
  return capabilities?.branches.readable ?? []
}

/** Филиалы, куда смотрящий вправе направить заявку (§4.13, §12.6 п.21). */
export function requestableBranches<T extends { id: number }>(
  capabilities: ViewerCapabilities,
  branches: T[],
): T[] {
  return branchesIn(branches, capabilities?.branches.requestable)
}

function branchesIn<T extends { id: number }>(branches: T[], allowed: number[] | undefined): T[] {
  if (allowed === undefined) return []
  const ids = new Set(allowed)
  return branches.filter((branch) => ids.has(branch.id))
}

// Принимает ли заявки из очереди в принципе (§2.1) — есть отдел квалификации.
// Конкретную заявку сверяет её `abilities`.
export function canAcceptRequestsFromQueue(capabilities: ViewerCapabilities): boolean {
  return (capabilities?.departments.qualified.length ?? 0) > 0
}

interface RecordScopeRefs {
  author: { id: number }
  branch: { id: number }
  department: { id: number } | null
}

// Управление записью (зеркало RecordPolicy#manageable?, §4.4) по наборам id прав (#704):
// автор в доступном филиале; холдинг/superadmin; иначе охват `manage_records`.
export function canManageRecord(
  capabilities: ViewerCapabilities,
  record: RecordScopeRefs,
  viewerId: number | null,
): boolean {
  if (capabilities === null) return false
  // holding_manager? — уровень 3 целиком, включая РКСО (у него нет manage_records).
  if (capabilities.superadmin || capabilities.level >= 3) return true
  if (record.author.id === viewerId && capabilities.branches.readable.includes(record.branch.id)) {
    return true
  }
  // verbs — открытый набор: ключа нет, когда роль verb'а не даёт вовсе (индекс-тип
  // схемы этого не выражает, поэтому наличие проверяем явно).
  if (!Object.hasOwn(capabilities.verbs, 'manage_records')) return false
  const reach = capabilities.verbs.manage_records

  return (
    reach.holding ||
    reach.branch_ids.includes(record.branch.id) ||
    (record.department !== null && reach.department_ids.includes(record.department.id))
  )
}

// Роли охвата (шаг 3 визарда) над served справочником: «Отдел» ещё фильтруется типом
// отдела (`department_kinds`, null — не ограничивает). Не загружен → [].
export function rolesForScope(
  catalog: readonly RoleCatalogEntry[],
  scope: PositionScope,
  departmentKind?: DepartmentKind,
): Role[] {
  return catalog
    .filter((entry) => {
      if (entry.grain !== scope) return false
      if (scope !== 'department') return true
      if (departmentKind === undefined) return false
      return entry.department_kinds === null || entry.department_kinds.includes(departmentKind)
    })
    .map((entry) => entry.role)
}

/** Локальная дата в ISO (YYYY-MM-DD) — для сравнения с date-полями API. */
export function todayISO(): string {
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

// Позиция актуальна: началась и не завершена (BE Position#active?); ended_on — дата,
// С КОТОРОЙ позиция НЕ действует (exclusive).
export function isPositionActive(position: Position, on: string = todayISO()): boolean {
  if (position.started_on > on) return false
  return position.ended_on === null || position.ended_on > on
}

export function activePositions(positions: Position[]): Position[] {
  return positions.filter((position) => isPositionActive(position))
}

// Завершённая позиция — гейт кнопок «сделать основной» / «завершить» (E25 §12.7).
export function isPositionEnded(position: Position): boolean {
  return position.ended_on !== null
}

/** Филиалы активных позиций (дефолт филиала в формах); позиция без филиала (#558) — мимо. */
export function positionBranchIds(positions: Position[]): number[] {
  return uniqueIds(activePositions(positions).map((position) => position.branch_id))
}

/** Отделы активных позиций (зеркало UserPermissions#department_ids). */
export function positionDepartmentIds(positions: Position[]): number[] {
  return uniqueIds(activePositions(positions).map((position) => position.department_id))
}

/** Дивизионы division-grain позиций (зеркало UserPermissions#division_ids). */
export function positionDivisionIds(positions: Position[]): number[] {
  return uniqueIds(activePositions(positions).map((position) => position.division_id))
}

function uniqueIds(ids: (number | null)[]): number[] {
  return [...new Set(ids.filter((id): id is number => id !== null))]
}

// Отдел основной активной позиции филиала — дефолт «Отдел» в формах (E490): main desc,
// id asc, как на бэке. null — такой позиции нет.
export function defaultDepartmentId(positions: Position[], branchId: number): number | null {
  return (
    activePositions(positions)
      .filter((position) => position.branch_id === branchId)
      .sort((a, b) => Number(b.main) - Number(a.main) || a.id - b.id)
      .map((position) => position.department_id)
      .find((id): id is number => id !== null) ?? null
  )
}
